import * as turf from '@turf/turf';
import type { Map as MapboxMap } from 'mapbox-gl';
import { type RefObject, useRef } from 'react';
import { registerMapEvent } from '@/libs/utils/registerMapEvents';
import { LayerId, SourceId } from '../constants';

type Opts = {
  lineLayerId: string; // your hovered lines layer id (e.g., 'metric-lines-layer')
  circleSourceId?: string; // defaults provided
  circleLayerId?: string;
  color?: string;
  radius?: number; // px
  strokeWidth?: number; // px
  map: RefObject<MapboxMap | null>;
};

export function useHoverCircleOnLine({
  lineLayerId,
  circleSourceId = SourceId.HOVER_TRAFFIC_CIRCLE,
  circleLayerId = LayerId.HOVER_TRAFFIC_CIRCLE,
  color = '#ffffff',
  radius = 5,
  strokeWidth = 2,
  map,
}: Opts) {
  const rafRef = useRef<number | null>(null);

  function updatePoint(lng: number, lat: number, feature: mapboxgl.MapboxGeoJSONFeature) {
    if (!map.current) return;

    const src = map.current.getSource(circleSourceId) as mapboxgl.GeoJSONSource;
    // nearest point on the hovered line
    const pt = turf.nearestPointOnLine(
      feature.geometry as GeoJSON.LineString | GeoJSON.MultiLineString,
      turf.point([lng, lat]),
      { units: 'meters' },
    );

    src.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: pt.geometry.coordinates as [number, number] },
        },
      ],
    });
  }

  function handleMouseMove(e: mapboxgl.MapMouseEvent & mapboxgl.Event) {
    if (!map.current) return;

    // throttle with rAF to avoid spamming setData
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const currentMap = map.current;
      if (!currentMap?.getLayer(lineLayerId)) return;
      const f = currentMap.queryRenderedFeatures(e.point, { layers: [lineLayerId] })[0];
      if (!f) return;
      updatePoint(e.lngLat.lng, e.lngLat.lat, f);
    });
  }

  function handleMouseLeave() {
    const src = map.current?.getSource(circleSourceId) as mapboxgl.GeoJSONSource | undefined;
    src?.setData({ type: 'FeatureCollection', features: [] });
  }

  function setupHoverTrafficCursor() {
    if (!map.current) return () => {};

    if (!map.current.getLayer(lineLayerId)) return () => {};

    // 1) Add point source/layer once
    if (!map.current.getSource(circleSourceId)) {
      map.current.addSource(circleSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    if (!map.current.getLayer(circleLayerId)) {
      map.current.addLayer({
        id: circleLayerId,
        type: 'circle',
        source: circleSourceId,
        slot: 'middle',
        paint: {
          // inverted look: black fill + white stroke
          'circle-radius': radius,
          'circle-color': '#000000',
          'circle-stroke-color': color,
          'circle-stroke-width': strokeWidth,
        },
      });
    }

    const cleanupMouseMove = registerMapEvent(map.current, 'mousemove', handleMouseMove);
    const cleanupMouseLeave = registerMapEvent(
      map.current,
      'mouseleave',
      lineLayerId,
      handleMouseLeave,
    );
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      cleanupMouseLeave();
      cleanupMouseMove();
    };
  }

  return { setupHoverTrafficCursor };
}
